import { useCallback, useEffect, useRef, useState } from 'react';

import type { SavedDataTableView, UseSavedDataTableViewsParams, UseSavedDataTableViewsResult } from './types';
import {
  clearSavedDataTableViews,
  createSavedViewsArrayValidator,
  readSavedDataTableViews,
  writeSavedDataTableViews,
} from './storage';
import { usePersistedView } from './use-persisted-view';

export const DEFAULT_MAX_SAVED_DATA_TABLE_VIEWS = 20;

export function useSavedDataTableViews<TFilters>({
  storageKey,
  filtersValidator,
  maxSavedViews = DEFAULT_MAX_SAVED_DATA_TABLE_VIEWS,
}: UseSavedDataTableViewsParams<TFilters>): UseSavedDataTableViewsResult<TFilters> {
  const { hasRemotePersistence, getSavedViewsByKeys, upsertSavedViews } = usePersistedView();

  const [savedViews, setSavedViews] = useState(() =>
    readSavedDataTableViews({
      storageKey,
      filtersValidator,
    }),
  );
  const savedViewsRef = useRef(savedViews);
  const inFlightPersistenceRequestIdRef = useRef(0);

  useEffect(() => {
    savedViewsRef.current = savedViews;
  }, [savedViews]);

  useEffect(() => {
    if (!hasRemotePersistence) return;

    const abortController = new AbortController();

    void (async () => {
      try {
        const remoteValues = await getSavedViewsByKeys({ keys: [storageKey] });
        if (abortController.signal.aborted) return;

        const remoteRaw = remoteValues.get(storageKey);
        const remoteSavedViews = createSavedViewsArrayValidator(filtersValidator)(remoteRaw) ? remoteRaw : null;
        if (remoteSavedViews !== null) {
          setSavedViews(remoteSavedViews);
          clearSavedDataTableViews(storageKey);
          return;
        }

        const currentSavedViews = savedViewsRef.current;
        if (currentSavedViews.length === 0) return;

        await upsertSavedViews({
          entries: [{ key: storageKey, value: currentSavedViews }],
        });
        clearSavedDataTableViews(storageKey);
      } catch {
        // best effort - keep localStorage as fallback
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [filtersValidator, getSavedViewsByKeys, hasRemotePersistence, storageKey, upsertSavedViews]);

  const persistSavedViews = useCallback(
    ({
      optimisticUpdated,
      applyToRemote,
    }: {
      optimisticUpdated: SavedDataTableView<TFilters>[];
      applyToRemote: (currentRemote: SavedDataTableView<TFilters>[]) => SavedDataTableView<TFilters>[];
    }) => {
      if (!hasRemotePersistence) {
        writeSavedDataTableViews({ storageKey, savedViews: optimisticUpdated });
        return;
      }

      const requestId = inFlightPersistenceRequestIdRef.current + 1;
      inFlightPersistenceRequestIdRef.current = requestId;

      void (async () => {
        try {
          const remoteValues = await getSavedViewsByKeys({ keys: [storageKey] });
          if (inFlightPersistenceRequestIdRef.current !== requestId) return;

          const remoteRaw = remoteValues.get(storageKey);
          const currentRemote = createSavedViewsArrayValidator(filtersValidator)(remoteRaw) ? remoteRaw : [];
          const mergedBaseline = mergeSavedViewsById({
            remote: currentRemote,
            optimistic: optimisticUpdated,
          });
          const nextRemote = applyToRemote(mergedBaseline);

          await upsertSavedViews({
            entries: [{ key: storageKey, value: nextRemote }],
          });
          if (inFlightPersistenceRequestIdRef.current !== requestId) return;
          clearSavedDataTableViews(storageKey);
        } catch {
          if (inFlightPersistenceRequestIdRef.current !== requestId) return;
          // best effort - keep localStorage as fallback
          writeSavedDataTableViews({ storageKey, savedViews: optimisticUpdated });
        }
      })();
    },
    [filtersValidator, getSavedViewsByKeys, hasRemotePersistence, storageKey, upsertSavedViews],
  );

  const saveView = useCallback(
    (name: string, filters: TFilters) => {
      const current = savedViewsRef.current;
      if (current.length >= maxSavedViews) return null;

      const next: SavedDataTableView<TFilters> = {
        id: crypto.randomUUID(),
        name,
        createdAt: Date.now(),
        filters,
      };
      const updated = [...current, next];
      savedViewsRef.current = updated;
      setSavedViews(updated);
      persistSavedViews({
        optimisticUpdated: updated,
        applyToRemote: (currentRemote) => {
          if (currentRemote.some((view) => view.id === next.id)) {
            return currentRemote;
          }

          return [...currentRemote, next];
        },
      });
      return next;
    },
    [maxSavedViews, persistSavedViews],
  );

  const updateView = useCallback(
    (id: string, filters: TFilters) => {
      const current = savedViewsRef.current;
      const updated = current.map((view) => (view.id === id ? { ...view, filters } : view));
      savedViewsRef.current = updated;
      setSavedViews(updated);
      persistSavedViews({
        optimisticUpdated: updated,
        applyToRemote: (currentRemote) => currentRemote.map((view) => (view.id === id ? { ...view, filters } : view)),
      });
    },
    [persistSavedViews],
  );

  const renameView = useCallback(
    (id: string, name: string) => {
      const current = savedViewsRef.current;
      const updated = current.map((view) => (view.id === id ? { ...view, name } : view));
      savedViewsRef.current = updated;
      setSavedViews(updated);
      persistSavedViews({
        optimisticUpdated: updated,
        applyToRemote: (currentRemote) => currentRemote.map((view) => (view.id === id ? { ...view, name } : view)),
      });
    },
    [persistSavedViews],
  );

  const deleteView = useCallback(
    (id: string) => {
      const current = savedViewsRef.current;
      const updated = current.filter((view) => view.id !== id);
      savedViewsRef.current = updated;
      setSavedViews(updated);
      persistSavedViews({
        optimisticUpdated: updated,
        applyToRemote: (currentRemote) => currentRemote.filter((view) => view.id !== id),
      });
      return updated;
    },
    [persistSavedViews],
  );

  return {
    savedViews,
    atLimit: savedViews.length >= maxSavedViews,
    saveView,
    updateView,
    renameView,
    deleteView,
  };
}

function mergeSavedViewsById<TFilters>({
  remote,
  optimistic,
}: {
  remote: SavedDataTableView<TFilters>[];
  optimistic: SavedDataTableView<TFilters>[];
}) {
  const optimisticIds = new Set(optimistic.map((view) => view.id));
  const remoteOnly = remote.filter((view) => !optimisticIds.has(view.id));
  return [...optimistic, ...remoteOnly];
}
